import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Mail, Lock, Loader2, Sparkles, ArrowRight, Binary, Phone } from 'lucide-react'
import { toast } from 'sonner'
import { sendCode, register as registerUser, getOemContext } from '@/service/api/auth'
import { RegionCascader, RegionState } from '@/components/oem_admin/RegionCascader'
import { cn, hashPassword } from '@/lib/utils'
import { useConfigStore } from '@/store/config'
import { useUserStore } from '@/store/user'
import {
  authCard,
  authCodeBtn,
  authFormInput,
  authIconBox,
  authInputIcon,
  authLink,
  authSubmitBtn,
} from '@/lib/ui-classes'
import { CaptchaGate } from '@/lib/captcha/CaptchaGate'

// 推广邀请人的落地 cookie（防止注册流程中刷新/跳转导致 ?inviter= 丢失）
const INVITER_COOKIE = 'inviter_code'

function readCookie(name: string): string {
  if (typeof document === 'undefined') return ''
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

const getSchema = () => {
  return z.object({
    username: z.string().refine((val) => {
      const config = useConfigStore.getState().config
      const isEmail = config?.is_email ?? true
      const isPhone = config?.is_phone ?? false

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const phoneRegex = /^1[3-9]\d{9}$/

      if (isEmail && isPhone) return emailRegex.test(val) || phoneRegex.test(val)
      if (isPhone) return phoneRegex.test(val)
      return emailRegex.test(val)
    }, { message: "请输入有效的账号格式" }),
    code: z.string().length(6, { message: "验证码必须是6位数字" }),
    password: z.string().min(6, { message: "密码至少需要6位" }),
  })
}

export default function Register() {
  const router = useRouter()
  const login = useUserStore((state) => state.login)
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const config = useConfigStore((state) => state.config)

  const [isOem, setIsOem] = useState(false)
  const [regionState, setRegionState] = useState<RegionState>({ level: 'district' })
  const [regionError, setRegionError] = useState<string | null>(null)
  const [inviter, setInviter] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!router.isReady) return
    getOemContext()
      .then((r) => setIsOem(!!r.is_oem))
      .catch(() => setIsOem(false))
    // 推广链接带 ?inviter=<user_id>，注册时一并上报（后端校验同 OEM 才生效）
    // 注册页为静态导出页，首帧 router.query 可能为空，必须等 router.isReady 后再读，否则会漏传
    let inv = typeof router.query.inviter === 'string' ? router.query.inviter.trim() : ''
    // URL 无参数时回退读取落地 cookie（防流程中刷新丢失）
    if (!inv && typeof document !== 'undefined') {
      const m = document.cookie.match(new RegExp('(?:^|; )' + INVITER_COOKIE + '=([^;]+)'))
      if (m) inv = decodeURIComponent(m[1])
    }
    if (inv) {
      setInviter(inv)
      // 落地 cookie，供注册接口兜底读取，避免静态页首帧漏传 ?inviter=
      document.cookie =
        INVITER_COOKIE + '=' + encodeURIComponent(inv) +
        '; path=/; max-age=' + 30 * 24 * 3600 + '; SameSite=Lax'
    }
  }, [router.isReady, router.query.inviter])

  const isEmail = config?.is_email ?? true
  const isPhone = config?.is_phone ?? false
  const label = isEmail && isPhone ? '邮箱 / 手机号' : (isPhone ? '手机号' : '邮箱地址')
  const placeholder = isEmail && isPhone ? '请输入邮箱或手机号' : (isPhone ? '请输入手机号' : '请输入邮箱地址')
  const InputIcon = isPhone && !isEmail ? Phone : Mail

  const registerSchema = getSchema()
  const { register, handleSubmit, formState: { errors }, getValues, trigger } = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
  })

  useEffect(() => {
    if (countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown])

  const sendVerificationCode = async (cap_token?: string) => {
    setSendingCode(true)

    try {
      const username = getValues('username')
      await sendCode({ username, type: 'register', cap_token })
      toast.success('验证码已发送!')
      setCountdown(60)
    } catch (error) {
      // Error handled by request helper
    } finally {
      setSendingCode(false)
    }
  }

  const onCaptchaSolved = async (token: string) => {
    await sendVerificationCode(token)
  }

  const onSendCodeBypass = async () => {
    await sendVerificationCode()
  }

  const onSubmit = async (data: z.infer<typeof registerSchema>) => {
    setLoading(true)
    try {
      if (isOem) {
        const { province, city, district } = regionState
        if (!province || !city || !district) {
          setRegionError('请选择所在省、市、区县')
          setLoading(false)
          return
        }
      }
      const resData = await registerUser({
        ...data,
        password: hashPassword(data.password),
        // 注册只选省/市/区县，大区由后端按省份自动反查写入 users.region_id
        ...(isOem && regionState.province
          ? {
              province_id: regionState.province,
              city_id: regionState.city,
              district_id: regionState.district,
            }
          : {}),
        // 推广邀请人（来自 ?inviter=，后端校验同 OEM）
        ...(inviter ? { inviter } : {}),
      })
      login(resData.token, resData.user)
      toast.success('注册成功')
      router.push('/projects')
    } catch (error) {
      // Error handled by request helper
    } finally {
      setLoading(false)
    }
  }

  const systemConfig = useConfigStore(state => state.config)
  const siteLogo = systemConfig?.site_logo || ''
  const siteName = systemConfig?.site_name || ''

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-black relative overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#07F4F2]/15 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#C1FAB8]/10 rounded-full blur-[120px]" />
        <div className="absolute inset-0 opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>

      <div className="relative z-10 w-full max-w-md p-8 mx-4">
        <div className="flex flex-col items-center mb-8 text-center">
          {siteLogo ? (
            <div className="mb-4 flex w-full max-w-xs justify-center px-2">
              <img
                src={siteLogo}
                alt="Site Logo"
                className="h-16 w-auto max-h-16 max-w-full object-contain rounded-xl"
              />
            </div>
          ) : (
            <div className={cn(authIconBox, 'h-16 w-16 mb-4')}>
              <Sparkles className="h-6 w-6 text-[#0a0a0a]" />
            </div>
          )}
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2">创建账号</h1>
          <p className="text-zinc-400">加入{siteName}，开启您的创作之旅</p>
        </div>

        <div className={authCard}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-300">{label}</Label>
              <div className="relative group">
                <InputIcon className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                <Input
                  id="username"
                  placeholder={placeholder}
                  type={isPhone && !isEmail ? "tel" : "text"}
                  disabled={loading || sendingCode}
                  {...register('username')}
                  className={authFormInput}
                />
              </div>
              {errors.username && (
                <p className="text-xs text-red-500 mt-1">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="code" className="text-zinc-300">验证码</Label>
              <div className="flex gap-3">
                <div className="relative group flex-1">
                  <Binary className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                  <Input
                    id="code"
                    placeholder="请输入验证码"
                    maxLength={6}
                    disabled={loading}
                    {...register('code')}
                    className={cn(authFormInput, 'font-mono tracking-widest')}
                  />
                </div>
                <CaptchaGate
                  scene="send_code"
                  mode="floating"
                  floatingPosition="bottom"
                  onBeforeFloating={async () => await trigger('username')}
                  onSolved={onCaptchaSolved}
                  onBypass={onSendCodeBypass}
                  onError={(message: string) => toast.error(message || '人机验证失败，请重试')}
                  trigger={
                    <button
                      type="button"
                      disabled={sendingCode || countdown > 0}
                      className={authCodeBtn}
                    >
                      {sendingCode ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#07F4F2]" />
                      ) : countdown > 0 ? (
                        <span className="font-mono">{countdown}s</span>
                      ) : (
                        "获取验证码"
                      )}
                    </button>
                  }
                />
              </div>
              {errors.code && (
                <p className="text-xs text-red-500 mt-1">{errors.code.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">密码</Label>
              <div className="relative group">
                <Lock className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                <Input
                  id="password"
                  type="password"
                  placeholder="请输入密码"
                  disabled={loading}
                  {...register('password')}
                  className={authFormInput}
                />
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>
              )}
            </div>

            {isOem && (
              <div className="space-y-2">
                <Label className="text-zinc-300">所在地区（省 / 市 / 区县）</Label>
                <div className="rounded-md border border-zinc-700 bg-zinc-900/50 p-3">
                  <RegionCascader
                    value={regionState}
                    hideLevel
                    hideRegion
                    onChange={(v) => {
                      setRegionState(v)
                      if (v.province && v.city && v.district) setRegionError(null)
                    }}
                  />
                </div>
                {regionError && <p className="text-xs text-red-500 mt-1">{regionError}</p>}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={authSubmitBtn}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  注册中...
                </>
              ) : (
                <>
                  立即注册
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="text-center text-sm">
              <span className="text-zinc-500">已有账号? </span>
              <Link href="/auth/login" className={authLink}>
                立即登录
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
